import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FlowLinkInBio } from "@/components/booking-flow/FlowLinkInBio";
import { ConfirmScene, type PassBooking } from "@/components/booking-flow/ConfirmationPass";
import { FlowDock, FlowTopBar } from "@/components/booking-flow/FlowScaffold";
import {
  BOOKING_THEMES,
  FLOW_DEFAULTS,
  flowThemeVars,
  loadThemeFonts,
  type BookingTheme,
} from "@/components/booking-flow/themes";
import ServiceSelectionStep from "@/components/magic-flow/ServiceSelectionStep";
import "@/landing.css";

type ScreenKind = "link" | "services" | "success";

type ShowcaseService = {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number;
  specialRequirements?: string;
};

type Showcase = {
  themeKey: keyof typeof BOOKING_THEMES;
  businessName: string;
  displayName: string;
  handle: string;
  bio: string;
  tagline: string;
  services: ShowcaseService[];
  links: Array<{ id: string; title: string; url: string; meta?: string }>;
};

const SHOWCASES: Showcase[] = [
  {
    themeKey: "rich_gradient",
    businessName: "Nami Studio",
    displayName: "Nami Studio",
    handle: "@namistudio.atx",
    bio: "Structured gel, soft-gel extensions, and tiny works of art.",
    tagline: "Nail artist · Austin",
    services: [
      { id: "nails-1", name: "Builder Gel Set", description: "Structured overlay with detailed cuticle care.", price: 85, duration: 90, specialRequirements: "Most booked" },
      { id: "nails-2", name: "Soft Gel Tips", description: "Full-cover extensions shaped and finished to you.", price: 95, duration: 105 },
      { id: "nails-3", name: "Signature Manicure", description: "Clean prep, gel color, and high-gloss finish.", price: 55, duration: 60 },
    ],
    links: [
      { id: "nail-art", title: "Nail art menu", url: "https://example.com/nail-art", meta: "View styles" },
      { id: "aftercare", title: "Aftercare guide", url: "https://example.com/aftercare", meta: "Keep them fresh" },
    ],
  },
  {
    themeKey: "cognac",
    businessName: "Maya Cuts",
    displayName: "Maya Cuts",
    handle: "@mayacuts",
    bio: "Precision fades, beard work, and a chair that runs on time.",
    tagline: "Barber · Round Rock",
    services: [
      { id: "barber-1", name: "Signature Fade", description: "Skin or shadow fade with a tailored finish.", price: 65, duration: 45, specialRequirements: "Most booked" },
      { id: "barber-2", name: "Fade + Beard", description: "Full cut, beard sculpt, and hot-towel finish.", price: 85, duration: 60 },
      { id: "barber-3", name: "Line Up", description: "Crisp edges and beard cleanup between cuts.", price: 35, duration: 25 },
    ],
    links: [
      { id: "portfolio", title: "Recent cuts", url: "https://example.com/cuts", meta: "See the work" },
      { id: "policies", title: "Before your appointment", url: "https://example.com/policies", meta: "Read policies" },
    ],
  },
  {
    themeKey: "emerald",
    businessName: "Built by Jordan",
    displayName: "Built by Jordan",
    handle: "@builtbyjordan",
    bio: "Strength coaching for people who want progress without gym chaos.",
    tagline: "Strength coach · Austin",
    services: [
      { id: "trainer-1", name: "Strength Session", description: "One-on-one coaching built around your goals.", price: 90, duration: 60, specialRequirements: "Most booked" },
      { id: "trainer-2", name: "Mobility Reset", description: "Focused movement work for stiff, tired bodies.", price: 70, duration: 45 },
      { id: "trainer-3", name: "Small Group", description: "Coach-led strength training with up to four people.", price: 45, duration: 60 },
    ],
    links: [
      { id: "results", title: "Client results", url: "https://example.com/results", meta: "See progress" },
      { id: "program", title: "Training approach", url: "https://example.com/program", meta: "How it works" },
    ],
  },
  {
    themeKey: "sky_ripple",
    businessName: "Still House",
    displayName: "Still House",
    handle: "@stillhouse.atx",
    bio: "Restorative bodywork for recovery, stress, and better movement.",
    tagline: "Massage therapy · East Austin",
    services: [
      { id: "massage-1", name: "Deep Tissue 60", description: "Focused pressure for tension and recovery.", price: 120, duration: 60, specialRequirements: "Most booked" },
      { id: "massage-2", name: "Swedish 60", description: "A slower full-body session for deep relaxation.", price: 105, duration: 60 },
      { id: "massage-3", name: "Hot Stone 75", description: "Warm-stone work paired with therapeutic massage.", price: 135, duration: 75 },
    ],
    links: [
      { id: "modalities", title: "Massage modalities", url: "https://example.com/modalities", meta: "Find your fit" },
      { id: "gift", title: "Gift a session", url: "https://example.com/gift", meta: "Send some calm" },
    ],
  },
];

const SCREENS: ScreenKind[] = ["services", "link", "success"];

function FiggyBrand({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`wabi-figgy-brand ${compact ? "is-compact" : ""}`}>
      <img src="/uploads/figgy_icon_transparent.png" alt="" />
      <strong>Figgy</strong>
    </span>
  );
}

function ServiceScreen({ theme, showcase }: { theme: BookingTheme; showcase: Showcase }) {
  const selectedService = showcase.services[0];
  const vars = flowThemeVars(theme) as React.CSSProperties;
  const bg = theme.flowBg || FLOW_DEFAULTS.bg;
  const ink = theme.flowInk || FLOW_DEFAULTS.ink;

  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: bg, color: ink, fontFamily: '"Inter Tight", system-ui, sans-serif', ...vars }}>
      <FlowTopBar step={1} totalSteps={3} theme={theme} businessName={showcase.businessName} onBack={() => {}} />
      <div style={{ flex: 1, overflow: "hidden", padding: "8px 24px 0" }}>
        <div style={{ maxWidth: 354, margin: "0 auto" }}>
          <h2 style={{ margin: 0, paddingTop: 6, fontFamily: theme.display, fontStyle: theme.displayItalic ? "italic" : "normal", fontWeight: theme.displayWeight, fontSize: 30, lineHeight: 1, letterSpacing: "-0.02em", color: ink }}>
            Select a service
          </h2>
          <div style={{ paddingTop: 28 }}>
            <ServiceSelectionStep selectedService={selectedService} onSelectService={() => {}} services={showcase.services} />
          </div>
        </div>
      </div>
      <div style={{ width: "100%", maxWidth: 402, margin: "0 auto" }}>
        <FlowDock cta="Continue" ctaSub={`${selectedService.duration} min · $${selectedService.price}`} onCta={() => {}} />
      </div>
    </div>
  );
}

function LinkScreen({ theme, showcase }: { theme: BookingTheme; showcase: Showcase }) {
  return (
    <FlowLinkInBio
      theme={theme}
      businessName={showcase.businessName}
      displayName={showcase.displayName}
      handle={showcase.handle}
      bio={showcase.bio}
      socialLinks={[{ platform: "instagram", url: "https://instagram.com" }, { platform: "tiktok", url: "https://tiktok.com" }]}
      customLinks={showcase.links}
      onBook={() => {}}
    />
  );
}

function SuccessScreen({ theme, showcase }: { theme: BookingTheme; showcase: Showcase }) {
  const vars = flowThemeVars(theme) as React.CSSProperties;
  const booking: PassBooking = {
    service: showcase.services[0].name,
    dateNum: "Jul 29",
    dateDay: "Wednesday",
    time: "2:15 PM",
    duration: `${showcase.services[0].duration} min`,
    guest: "Josh",
    confirmation: "FGY-4821",
  };

  return (
    <div style={{ position: "absolute", inset: 0, background: theme.flowBg || FLOW_DEFAULTS.bg, fontFamily: '"Inter Tight", system-ui, sans-serif', ...vars }}>
      <ConfirmScene stage="expanded" theme={theme} visible booking={booking} onTap={() => {}} onClose={() => {}} onAddToCalendar={() => {}} onManage={() => {}} businessName={showcase.businessName} businessTagline={showcase.tagline} />
    </div>
  );
}

function ProductionScreen({ kind, theme, showcase }: { kind: ScreenKind; theme: BookingTheme; showcase: Showcase }) {
  useEffect(() => {
    loadThemeFonts(theme);
  }, [theme]);

  return (
    <div className="wabi-production-canvas">
      {kind === "services" && <ServiceScreen theme={theme} showcase={showcase} />}
      {kind === "link" && <LinkScreen theme={theme} showcase={showcase} />}
      {kind === "success" && <SuccessScreen theme={theme} showcase={showcase} />}
    </div>
  );
}

export function WabiHero({ embedded = false }: { embedded?: boolean }) {
  const [activeTheme, setActiveTheme] = useState(0);
  const [activeScreen, setActiveScreen] = useState(0);
  const [paused, setPaused] = useState(false);

  const showcase = SHOWCASES[activeTheme];
  const theme = BOOKING_THEMES[showcase.themeKey];

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActiveScreen((current) => {
        const next = (current + 1) % SCREENS.length;
        if (next === 0) setActiveTheme((themeIndex) => (themeIndex + 1) % SHOWCASES.length);
        return next;
      });
    }, 3200);
    return () => window.clearInterval(timer);
  }, [paused]);

  const cardOrder = useMemo(
    () => SCREENS.map((kind, index) => ({ kind, position: (index - activeScreen + SCREENS.length) % SCREENS.length })),
    [activeScreen],
  );

  return (
    <div className={`wabi-landing ${embedded ? "is-embedded" : ""}`}>
      <style>{`
        .wabi-landing{min-height:100vh;background:#f2f2ef;color:#222;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;overflow:hidden}.wabi-landing.is-embedded{min-height:auto}.wabi-landing *{box-sizing:border-box}.wabi-landing a{text-decoration:none;color:inherit}.wabi-header{position:absolute;z-index:30;top:34px;left:0;right:0;width:min(1180px,100%);margin:0 auto;padding:0 40px;display:flex;align-items:center;justify-content:space-between}.wabi-landing.is-embedded .wabi-header{display:none}.wabi-figgy-brand{display:inline-flex;align-items:center;gap:11px;color:#777774;font-size:35px;line-height:1;letter-spacing:-.05em}.wabi-figgy-brand img{width:42px;height:42px;object-fit:contain;filter:grayscale(1);opacity:.72}.wabi-figgy-brand strong{font-weight:780}.wabi-figgy-brand.is-compact{font-size:23px;color:#242422;gap:8px}.wabi-figgy-brand.is-compact img{width:24px;height:24px;filter:none;opacity:1}.wabi-cta{display:inline-flex;align-items:center;justify-content:center;min-width:184px;height:66px;padding:0 26px;border-radius:999px;background:rgba(255,255,255,.9);border:1px solid rgba(255,255,255,.98);box-shadow:0 25px 52px rgba(0,0,0,.15),inset 0 1px 0 #fff;color:#2c2c2a;font-size:18px;font-weight:710;transition:transform .45s cubic-bezier(.16,1,.3,1),box-shadow .45s ease}.wabi-cta:hover{transform:translateY(-2px);box-shadow:0 30px 60px rgba(0,0,0,.18),inset 0 1px 0 #fff}.wabi-cta:active{transform:scale(.985)}.wabi-hero{position:relative;min-height:1180px;padding:190px 24px 0;overflow:hidden;background:linear-gradient(180deg,#f3f3f1 0%,#f1f1ee 70%,#ecece8 100%)}.wabi-landing.is-embedded .wabi-hero{padding-top:128px}.wabi-copy{position:relative;z-index:10;width:min(860px,calc(100% - 28px));margin:0 auto;text-align:center}.wabi-copy h1{margin:0;font-family:"Iowan Old Style","Baskerville","Times New Roman",serif;font-size:clamp(72px,8.4vw,122px);line-height:.84;letter-spacing:-.07em;font-weight:400;color:#222}.wabi-copy h1 span{display:block}.wabi-copy p{max-width:790px;margin:48px auto 0;color:#777774;font-size:clamp(21px,2.05vw,31px);line-height:1.43;font-weight:650;letter-spacing:-.036em}.wabi-copy-cta{display:inline-flex;align-items:center;justify-content:center;min-height:58px;margin-top:34px;padding:0 24px;border-radius:999px;background:#2c2c30;color:#fff;font-size:17px;font-weight:760;box-shadow:inset 0 1px 0 rgba(255,255,255,.2),0 12px 30px rgba(20,20,22,.18);transition:transform .25s ease,background-color .25s ease}.wabi-copy-cta:hover{background:#18181b;transform:translateY(-1px)}.wabi-theme-people{display:inline-flex;align-items:center;vertical-align:middle;margin-left:9px;transform:translateY(-2px)}.wabi-theme-people button{width:38px;height:38px;margin-left:-8px;padding:0;border:3px solid #f2f2ef;border-radius:50%;display:grid;place-items:center;color:#fff;font-size:9px;font-weight:850;cursor:pointer;box-shadow:0 5px 14px rgba(0,0,0,.13);transition:transform .38s cubic-bezier(.16,1,.3,1),box-shadow .38s ease}.wabi-theme-people button:first-child{margin-left:0}.wabi-theme-people button:hover{transform:translateY(-2px) scale(1.04)}.wabi-theme-people button.active{box-shadow:0 0 0 2px #f2f2ef,0 0 0 4px #6d6d69,0 7px 18px rgba(0,0,0,.16);transform:translateY(-2px)}.wabi-stage{position:relative;z-index:6;width:min(940px,100%);height:790px;margin:72px auto 0;perspective:1200px}.wabi-screen-card{position:absolute;width:330px;height:717px;border-radius:35px;overflow:hidden;background:#fff;border:1px solid rgba(255,255,255,.95);box-shadow:0 38px 80px rgba(38,36,31,.18),0 1px 0 rgba(255,255,255,.9) inset;transition:left .9s cubic-bezier(.16,1,.3,1),right .9s cubic-bezier(.16,1,.3,1),top .9s cubic-bezier(.16,1,.3,1),transform .9s cubic-bezier(.16,1,.3,1),opacity .7s ease,filter .7s ease;transform-origin:50% 60%;will-change:transform,left,right,top;isolation:isolate}.wabi-screen-card.position-0{z-index:8;left:50%;right:auto;top:0;transform:translateX(-50%) rotate(0deg) scale(1);opacity:1;filter:none;pointer-events:auto}.wabi-screen-card.position-1{z-index:3;left:58px;right:auto;top:126px;transform:rotate(-7deg) scale(.91);opacity:.92;filter:saturate(.92);pointer-events:none}.wabi-screen-card.position-2{z-index:2;left:auto;right:58px;top:108px;transform:rotate(7deg) scale(.91);opacity:.9;filter:saturate(.9);pointer-events:none}.wabi-production-canvas{position:relative;width:402px;height:874px;transform:scale(.821);transform-origin:top left;overflow:hidden;background:#f4f1eb}.wabi-decoration{position:absolute;z-index:1;pointer-events:none;filter:drop-shadow(0 18px 25px rgba(0,0,0,.08));animation:wabiDrift 8s ease-in-out infinite}.wabi-map{right:105px;top:-22px;width:126px;height:88px;border-radius:8px;background:linear-gradient(145deg,#d2c6aa,#f2ead9);transform:rotate(12deg)}.wabi-map:before,.wabi-map:after{content:"";position:absolute;height:2px;border-radius:999px;background:rgba(78,70,49,.22)}.wabi-map:before{width:76px;left:17px;top:26px;transform:rotate(-17deg)}.wabi-map:after{width:52px;left:34px;top:49px;transform:rotate(19deg)}.wabi-ticket{left:12px;bottom:90px;width:128px;height:72px;border-radius:12px;background:linear-gradient(145deg,#f2efe8,#dcd6ca);transform:rotate(-14deg);animation-delay:-3s}.wabi-ticket:before,.wabi-ticket:after{content:"";position:absolute;top:25px;width:22px;height:22px;border-radius:50%;background:#eeeeea}.wabi-ticket:before{left:-11px}.wabi-ticket:after{right:-11px}.wabi-float{position:absolute;z-index:12;display:flex;align-items:center;gap:10px;padding:13px 15px;border-radius:20px;background:rgba(255,255,255,.78);border:1px solid rgba(255,255,255,.96);backdrop-filter:blur(12px);box-shadow:0 20px 42px rgba(0,0,0,.1),inset 0 1px 0 #fff;color:#343431;font-size:13px;font-weight:720;animation:wabiFloat 7.4s ease-in-out infinite}.wabi-float small{display:block;margin-top:2px;color:#777774;font-size:11px;font-weight:650}.wabi-float-icon{width:40px;height:40px;border-radius:50%;display:grid;place-items:center;background:#f5f5f1;box-shadow:inset 0 0 0 1px rgba(0,0,0,.05);font-size:18px}.wabi-float.one{left:-4px;top:350px;transform:rotate(-8deg);animation-delay:-1s}.wabi-float.two{right:-5px;top:390px;transform:rotate(6deg);animation-delay:-4s}.wabi-float.three{left:120px;bottom:14px;transform:rotate(-3deg);animation-delay:-5.5s}@keyframes wabiFloat{50%{margin-top:-8px}}@keyframes wabiDrift{50%{margin-top:-7px;filter:drop-shadow(0 24px 30px rgba(0,0,0,.1))}}@media(max-width:900px){.wabi-header{padding:0 24px}.wabi-hero{min-height:1160px}.wabi-stage{width:min(820px,100%)}.wabi-screen-card.position-1{left:10px}.wabi-screen-card.position-2{right:10px}.wabi-float.one{left:-12px}.wabi-float.two{right:-10px}}@media(max-width:620px){.wabi-header{top:24px;padding:0 17px}.wabi-figgy-brand{font-size:29px;gap:8px}.wabi-figgy-brand img{width:34px;height:34px}.wabi-cta{min-width:152px;height:56px;padding:0 20px;font-size:16px}.wabi-hero{min-height:1110px;padding:145px 12px 0}.wabi-landing.is-embedded .wabi-hero{padding-top:112px}.wabi-copy{width:100%}.wabi-copy h1{font-size:clamp(56px,14.5vw,76px);line-height:.86}.wabi-copy p{max-width:375px;margin-top:34px;font-size:clamp(19px,5.4vw,25px);line-height:1.45}.wabi-copy-cta{min-height:54px;margin-top:27px;padding:0 21px;font-size:16px}.wabi-theme-people{display:flex;justify-content:center;margin:12px 0 0}.wabi-theme-people button{width:34px;height:34px;font-size:8px}.wabi-stage{height:680px;margin-top:56px}.wabi-screen-card{width:260px;height:566px;border-radius:29px}.wabi-production-canvas{transform:scale(.647)}.wabi-screen-card.position-0{top:0}.wabi-screen-card.position-1{left:-62px;top:176px;transform:rotate(-8deg) scale(.9)}.wabi-screen-card.position-2{right:-62px;top:164px;transform:rotate(8deg) scale(.9)}.wabi-map{right:2px;top:105px;width:100px;height:70px}.wabi-ticket{left:-18px;bottom:76px;width:104px;height:60px}.wabi-float{padding:10px 12px;border-radius:18px;font-size:11px}.wabi-float-icon{width:34px;height:34px;font-size:16px}.wabi-float.one{left:-20px;top:336px}.wabi-float.two{right:-25px;top:380px}.wabi-float.three{left:12px;bottom:3px}}@media(max-width:390px){.wabi-cta{min-width:142px}.wabi-copy h1{font-size:54px}.wabi-copy p{font-size:20px}.wabi-stage{margin-top:52px}.wabi-screen-card.position-1{left:-78px}.wabi-screen-card.position-2{right:-78px}.wabi-float.three{display:none}}@media(prefers-reduced-motion:reduce){.wabi-cta,.wabi-copy-cta,.wabi-theme-people button,.wabi-screen-card{transition:none}.wabi-decoration,.wabi-float{animation:none}}
      `}</style>

      {!embedded && (
        <nav className="wabi-header">
          <FiggyBrand />
          <Link to="/magic-onboarding-v2?source=invite_cta" className="wabi-cta">Create your page</Link>
        </nav>
      )}

      <section className="wabi-hero">
        <div className="wabi-copy">
          <h1><span>Stop losing bookings</span><span>to a bad link.</span></h1>
          <p>Turn more Instagram visitors into paying clients with a booking page built to convert—free to start.</p>
          <Link to="/magic-onboarding-v2?source=homepage_hero" className="wabi-copy-cta">Build my free page →</Link>
        </div>

        <div className="wabi-stage" onPointerEnter={() => setPaused(true)} onPointerLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}>
          <div className="wabi-decoration wabi-map" />
          <div className="wabi-decoration wabi-ticket" />

          {cardOrder.map(({ kind, position }) => (
            <div key={kind} className={`wabi-screen-card position-${position}`}>
              <ProductionScreen kind={kind} theme={theme} showcase={showcase} />
            </div>
          ))}

          <div className="wabi-float one"><span className="wabi-float-icon">↗</span><div>Link in bio<small>{showcase.handle}</small></div></div>
          <div className="wabi-float two"><span className="wabi-float-icon">✓</span><div>Booking confirmed<small>{showcase.services[0].name}</small></div></div>
          <div className="wabi-float three"><span className="wabi-float-icon">✦</span><div>{theme.name} theme<small>One style, every screen</small></div></div>
        </div>
      </section>
    </div>
  );
}

export default function WabiInspiredLanding() {
  return <WabiHero />;
}
